import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, ScanLine, Search, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import VehicleDetailDialog from "@/components/VehicleDetailDialog";
import { useConfirm } from "@/components/ConfirmDialog";
import ScanRegistrationDialog from "@/components/ScanRegistrationDialog";

export default function Vehicles() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({
    customer_id: "", brand: "", model: "", license_plate: "",
    vin: "", mileage: 0, first_registration: "", fuel_type: "", engine: "",
  });
  const canEdit = user?.role === "owner" || user?.role === "admin";

  const load = async () => {
    const [v, c] = await Promise.all([api.get("/vehicles"), api.get("/customers")]);
    setItems(v.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/vehicles", { ...form, mileage: parseInt(form.mileage || 0, 10) });
      toast.success("OK");
      setOpen(false);
      setForm({ customer_id: "", brand: "", model: "", license_plate: "", vin: "", mileage: 0, first_registration: "", fuel_type: "", engine: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!(await confirm())) return;
    await api.delete(`/vehicles/${id}`);
    load();
  };

  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((v) => {
      const haystack = [
        v.brand, v.model, v.license_plate, v.vin,
        v.fuel_type, v.engine, v.first_registration,
        v.mileage != null ? String(v.mileage) : "",
        customerName(v.customer_id),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(s);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, customers, q]);

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("vehicles.title")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">{t("vehicles.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")}
              className="pl-9 bg-[#171B24] border-[#2E3241] w-64" data-testid="vehicles-search" />
          </div>
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => setScanOpen(true)}
              className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
              data-testid="scan-registration-btn"
            >
              <ScanLine className="h-4 w-4 mr-1" /> {t("scan.button")}
            </Button>
          )}
          {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-500 hover:bg-blue-600" data-testid="new-vehicle-btn"><Plus className="h-4 w-4 mr-1" /> {t("vehicles.new_vehicle")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#21252E] border-[#2E3241] text-white max-w-xl">
              <DialogHeader><DialogTitle>{t("vehicles.new_vehicle")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">{t("contracts.customer")}</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger className="bg-[#171B24] border-[#2E3241]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#21252E] border-[#2E3241] text-white max-h-60">
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {[
                  ["brand", t("vehicles.brand")], ["model", t("vehicles.model")],
                  ["license_plate", t("vehicles.plate")], ["vin", t("vehicles.vin")],
                  ["mileage", t("vehicles.mileage")], ["first_registration", t("vehicles.first_registration")],
                  ["fuel_type", t("vehicles.fuel")], ["engine", t("vehicles.engine")],
                ].map(([k, l]) => (
                  <div key={k}>
                    <Label className="text-xs">{l}</Label>
                    <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="bg-[#171B24] border-[#2E3241]" data-testid={`vehicle-${k}`} />
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                <Button onClick={submit} className="bg-blue-500 hover:bg-blue-600" data-testid="create-vehicle-submit">{t("common.create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <div className="surface rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#2A2F3A] text-zinc-400">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("vehicles.brand")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("vehicles.model")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("vehicles.plate")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("contracts.customer")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("vehicles.mileage")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={6} className="text-center text-zinc-500 py-8">{t("common.empty")}</td></tr>
              : filtered.map((v) => (
              <tr key={v.id}
                onClick={() => setDetailId(v.id)}
                className="border-t border-[#2E3241] hover:bg-[#2A2F3A] cursor-pointer"
                data-testid={`vehicle-row-${v.id}`}>
                <td className="px-4 py-3 font-medium">{v.brand}</td>
                <td className="px-4 py-3 text-zinc-300">{v.model}</td>
                <td className="px-4 py-3 text-zinc-300 font-mono-stat">{v.license_plate}</td>
                <td className="px-4 py-3 text-zinc-400">{customerName(v.customer_id)}</td>
                <td className="px-4 py-3 text-zinc-400 font-mono-stat">{v.mileage} km</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>{canEdit && (
                  <Button size="icon" variant="ghost" onClick={() => del(v.id)} className="text-zinc-400 hover:text-red-400" data-testid={`vehicle-del-${v.id}`}><Trash2 className="h-4 w-4" /></Button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <VehicleDetailDialog
        open={!!detailId}
        onOpenChange={(o) => !o && setDetailId(null)}
        vehicleId={detailId}
        canEdit={user?.role === "owner" || user?.role === "admin"}
        onSaved={load}
      />

      <ScanRegistrationDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        customers={customers}
        onSaved={load}
      />
    </div>
  );
}
